import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import AdminDashboard from './pages/admin/Dashboard';
import Workers from './pages/admin/Workers';
import Sites from './pages/admin/Sites';
import AdminAttendance from './pages/admin/Attendance';
import AdminSalary from './pages/admin/Salary';
import AdminAdvances from './pages/admin/Advances';
import AdminMonthlyReport from './pages/admin/MonthlyReport';
import WorkerDashboard from './pages/worker/Dashboard';
import WorkerAttendance from './pages/worker/Attendance';
import WorkerSalary from './pages/worker/Salary';
import WorkerAdvance from './pages/worker/Advance';
import AdminAnnouncements from './pages/admin/Announcements';
import Profile from './pages/shared/Profile';

const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/worker/dashboard'} replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/worker/dashboard'} replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

              <Route path="/admin" element={
                <ProtectedRoute role="admin"><MainLayout /></ProtectedRoute>
              }>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="workers" element={<Workers />} />
                <Route path="sites" element={<Sites />} />
                <Route path="attendance" element={<AdminAttendance />} />
                <Route path="salary" element={<AdminSalary />} />
                <Route path="advances" element={<AdminAdvances />} />
                <Route path="reports/monthly" element={<AdminMonthlyReport />} />
                <Route path="announcements" element={<AdminAnnouncements />} />
                <Route path="profile" element={<Profile />} />
              </Route>

              <Route path="/worker" element={
                <ProtectedRoute role="worker"><MainLayout /></ProtectedRoute>
              }>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<WorkerDashboard />} />
                <Route path="attendance" element={<WorkerAttendance />} />
                <Route path="salary" element={<WorkerSalary />} />
                <Route path="advance" element={<WorkerAdvance />} />
                <Route path="profile" element={<Profile />} />
              </Route>

              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
